import React from 'react';
import { useLingui } from '@lingui/react/macro';

import { AVAILABILITY_PREFERENCES_TYPE_IDS } from '@rover/constants/src/translations/sitterProfilePage.constants';
import AlertInfo from '@rover/icons/svg/alert-info.svg';
import { Flex } from '@rover/kibble/core';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import { filterServicesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { SitterProfileVersionOptions } from '../../constants';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { groupServicesByLocationType, isHostedServiceType } from '../../utils/utils';
import AccordionSection from '../AccordionSection/AccordionSection';
import ServiceCapacity from '../AvailabilityCalendarSection/ServiceCapacity';
import ServicePreferencesSection, {
  petPreferencesTypes,
} from '../ServiceSection/ServicePreferencesSection';

import { SitterProfilePetsSitterCanHostExpandEvent } from './analyticsEvents';

const PetTypePreferencesSection = (): JSX.Element | null => {
  const { t } = useLingui();
  const { serviceType, sitterData } = useRevampProfileContext();
  const brandPets = useBrandSpecificPets();

  const availableServices = filterServicesBasedOnPetTypes(sitterData.services, brandPets);
  const { hostingActiveServices, travelingActiveServices } =
    groupServicesByLocationType(availableServices);

  const isHosted = isHostedServiceType(serviceType);

  const preferencesType = isHosted ? petPreferencesTypes.HOSTING : petPreferencesTypes.TRAVELING;
  const preferences = isHosted
    ? sitterData.providerProfile?.hostingServicePreferences
    : sitterData.providerProfile?.travelingServicePreferences;
  const activeServices = isHosted ? hostingActiveServices : travelingActiveServices;

  if (!preferences) return null;

  const selectedServiceCapacity = serviceType
    ? sitterData.serviceCapacity.find(
        (capacity) => capacity.calendarId === AVAILABILITY_PREFERENCES_TYPE_IDS()[serviceType]
      )
    : null;

  const sitterName = sitterData.firstName;
  const sectionTitle = isHosted
    ? t`Pets ${sitterName} can host`
    : t`Pets ${sitterName} can care for`;

  return (
    <AccordionSection
      sectionName="pet-type-preferences"
      title={sectionTitle}
      onExpand={() => emitAnalyticsEvent(new SitterProfilePetsSitterCanHostExpandEvent())}
      body={
        <>
          <ServicePreferencesSection
            sitterName={sitterData.firstName}
            preferences={preferences}
            preferencesType={preferencesType}
            activeServices={activeServices}
            weightLabel={sitterData.countryWeightLabel}
            dogSizes={sitterData.countryDogSizes}
            sitterProfileVersion={SitterProfileVersionOptions.REVAMP}
          />
          {selectedServiceCapacity && (
            <Flex alignItems="center" py="2x">
              <Flex mr="3x">
                <AlertInfo width={24} height={24} />
              </Flex>
              <ServiceCapacity
                sitterName={sitterName}
                capacity={selectedServiceCapacity}
                textSize="200"
              />
            </Flex>
          )}
        </>
      }
    />
  );
};

export default PetTypePreferencesSection;
